// Import Dependencies
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Import Components
import Expandable from '../Expandable';
import Button from '../Button';

// Import Utilities
import {
  getClassNames, isArrayWithLength, isMediaMatched, fetchData,
} from '../../../utils';

const FilterBar = ({
  apiUrl, cyop, resetFilters, ...props
}) => {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState([]);
  const contentRef = useRef(null);
  const mediaQuery = isMediaMatched(1200);

  const handleToggle = () => {
    setActive(!active);
  };

  useEffect(() => {
    async function getData() {
      const data = await fetchData(apiUrl);
      let updatedFilters = data && data.meta;

      if (updatedFilters && updatedFilters.some(filter => filter.id === 'availableMonths')) {
        updatedFilters = updatedFilters.filter(filter => filter.id !== 'availableMonths');
      }
      setLoading(!loading);
      setFilters(updatedFilters);
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;

    if (mounted && filters.length) {
      if (!mediaQuery) {
        contentRef.current.style.maxHeight = active ? '9000px' : '0px';

        setTimeout(() => {
          if (active) {
            contentRef.current.style.overflow = 'visible';
          }
        }, 300);

        if (!active) {
          contentRef.current.style.overflow = 'hidden';
        }
      }
    }

    return () => mounted = false; // eslint-disable-line
  }, [filters.length, mediaQuery, contentRef, active]);

  return (
    <>
      {!loading
        && (
          <div className={getClassNames(props, 'c-filterbar  js-filterbar', {
            'is-open': active,
          })}
          >
            {(!filters || !filters.length) && !loading && <p className="u-heading-3">No filters found</p>}

            {filters && filters.length > 0
              && (
                // mobile menu
                <div className="u-hide@large">
                  <Button calendar onClick={handleToggle}>
                    <span>Categories</span>
                    <span className={`u-hide@large ${cyop ? 'c-filterbar__button--translate-mobile-label' : ''}`}>{active ? 'Close' : 'Filter'}</span>
                  </Button>

                  {!cyop && (
                    <Button calendar clickable noBackground onClick={() => resetFilters(true)}>
                      <span className="c-filterbar__button-text--white u-text-underline">Reset all filters</span>
                    </Button>
                  )}

                  <div className="c-filterbar__filters" ref={contentRef}>
                    {isArrayWithLength(filters)
                      && filters.map((filter, index) => (
                        <Expandable index={index} key={filter.id} {...filter} {...props} />
                      ))}
                  </div>
                </div>
              )}

            {filters
              && filters.length > 0
              && (
                // desktop menu (1200px+)
                <>
                  <div className={getClassNames(props, 'c-filterbar__desktop-version', {
                    'c-filterbar__desktop-version--fixed-height': cyop,
                  })}
                  >
                    {!cyop && (
                      <Button calendar onClick={handleToggle}>
                        <span>Filter By</span>
                      </Button>
                    )}

                    <div className="c-filterbar__filters">
                      {isArrayWithLength(filters)
                        && filters.map((filter, index) => (
                          !filter.advanced
                            && <Expandable index={index} key={filter.id} cyop={cyop} {...filter} {...props} />
                        ))}
                    </div>
                  </div>

                  <div className="c-filterbar__desktop-version c-filterbar__desktop-version--advanced">
                    {!cyop && (
                      <Button calendar onClick={handleToggle}>
                        <span className="c-filterbar__advanced-text">Advanced Filters</span>
                      </Button>
                    )}

                    <div className="c-filterbar__filters">
                      {isArrayWithLength(filters)
                        && filters.map((filter, index) => (
                          filter.advanced
                          && (
                            <Expandable
                              index={index}
                              cyopAdvanced={cyop}
                              key={filter.id}
                              advanced
                              {...filter}
                              {...props}
                            />
                          )
                        ))}

                      {!cyop && (
                        <Button calendar clickable onClick={() => resetFilters(true)}>
                          <span className="c-filterbar__advanced-text c-filterbar__advanced-text--centred u-text-underline">Reset all filters</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
    </>
  );
};

FilterBar.defaultProps = {
  cyop: false,
};

FilterBar.propTypes = {
  apiUrl: PropTypes.string.isRequired,
  cyop: PropTypes.bool,
  resetFilters: PropTypes.func.isRequired,
};

export default FilterBar;
